/**
 * Schwebender WhatsApp-Button „Kurze Frage?“ (ersetzt den schwebenden
 * Terminbutton). Bindeglied zwischen Erstgespräch und gar keiner Anfrage.
 *
 * - Desktop (ab lg): Button mit Text, öffnet ein kleines Fenster mit QR-Code
 *   fürs Handy und „Am Computer schreiben“ (Desktop-App bzw. WhatsApp Web).
 * - Handy/Tablet: runder Button nur mit Logo, öffnet WhatsApp direkt. Der
 *   Hoch-Button rückt links daneben (siehe ScrollToTopButton).
 *
 * Nummer aus dem Admin (Kontakte → WhatsApp). Ohne Nummer kein Button.
 * Ereignis `whatsapp_klick` mit `weg`: fenster (QR-Fenster geöffnet),
 * computer, mobil.
 */
import { useEffect, useId, useRef, useState } from "react";
import { X } from "lucide-react";
import { pushDataLayerEvent } from "@/lib/datalayer";
import { useWhatsAppContact } from "./use-whatsapp-contact";
import { WhatsAppIcon } from "./WhatsAppIcon";

/** QR-Code im Browser erzeugt; die Bibliothek lädt erst beim Öffnen des Fensters. */
function QrCode({ value, size = 176 }: { value: string; size?: number }) {
  const [cells, setCells] = useState<{ count: number; path: string } | null>(null);
  useEffect(() => {
    let active = true;
    void import("qrcode-generator").then(({ default: qrcode }) => {
      const qr = qrcode(0, "M");
      qr.addData(value);
      qr.make();
      const count = qr.getModuleCount();
      const dark: string[] = [];
      for (let r = 0; r < count; r++) {
        for (let c = 0; c < count; c++) if (qr.isDark(r, c)) dark.push(`M${c} ${r}h1v1h-1z`);
      }
      if (active) setCells({ count, path: dark.join("") });
    });
    return () => {
      active = false;
    };
  }, [value]);
  if (!cells) {
    return (
      <div style={{ width: size, height: size }} className="rounded-md bg-secondary" aria-hidden />
    );
  }
  const margin = 2;
  const box = cells.count + margin * 2;
  return (
    <svg
      viewBox={`${-margin} ${-margin} ${box} ${box}`}
      width={size}
      height={size}
      role="img"
      aria-label="QR-Code zum WhatsApp-Chat"
      shapeRendering="crispEdges"
      className="rounded-md bg-white"
    >
      <path d={cells.path} fill="#212121" />
    </svg>
  );
}

const SAFE_BOTTOM = { bottom: "max(1.5rem, calc(env(safe-area-inset-bottom) + 0.75rem))" };

export function WhatsAppFloatingButton() {
  const { visible, chatUrl } = useWhatsAppContact();
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  // Fenster schließen mit Escape und Klick daneben.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    const onPointer = (e: PointerEvent) => {
      const target = e.target as Node;
      if (panelRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [open]);

  if (!visible || !chatUrl) return null;

  const toggleDesktop = () => {
    if (!open) pushDataLayerEvent({ event: "whatsapp_klick", weg: "fenster" });
    setOpen((v) => !v);
  };

  return (
    <>
      {/* Desktop: Button mit Text und Fenster */}
      <div className="fixed right-6 z-40 hidden lg:block" style={SAFE_BOTTOM}>
        {open && (
          <div
            ref={panelRef}
            id={panelId}
            role="dialog"
            aria-label="Kurze Frage per WhatsApp"
            className="absolute bottom-full right-0 mb-3 w-[300px] rounded-lg border border-border bg-background p-5 text-left shadow-xl shadow-black/15"
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Schließen"
              className="absolute right-3 top-3 rounded-full p-1 text-muted-foreground transition hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-button"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
            <p className="pr-6 font-display text-base font-extrabold text-primary">
              Kurze Frage? Schreiben Sie uns auf WhatsApp.
            </p>
            <div className="mt-4 flex flex-col items-center">
              <QrCode value={chatUrl} />
              <p className="mt-2 font-body text-sm text-muted-foreground">Mit dem Handy scannen</p>
            </div>
            <a
              href={chatUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => pushDataLayerEvent({ event: "whatsapp_klick", weg: "computer" })}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-3 font-display text-sm font-extrabold text-primary-foreground transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-button focus-visible:ring-offset-2"
            >
              <WhatsAppIcon className="h-4 w-4" />
              Am Computer schreiben
            </a>
          </div>
        )}
        <button
          ref={triggerRef}
          type="button"
          onClick={toggleDesktop}
          aria-expanded={open}
          aria-controls={open ? panelId : undefined}
          aria-haspopup="dialog"
          className="inline-flex max-h-[20vh] items-center gap-2 rounded-full bg-brand-button px-[1.5625rem] py-[0.9375rem] font-display text-[1.09375rem] leading-none font-extrabold text-brand-button-foreground shadow-lg shadow-black/20 transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-button/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          <WhatsAppIcon className="h-5 w-5" />
          Kurze Frage?
        </button>
      </div>

      {/* Handy und Tablet: nur Logo, öffnet WhatsApp direkt */}
      <a
        href={chatUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Per WhatsApp schreiben"
        onClick={() => pushDataLayerEvent({ event: "whatsapp_klick", weg: "mobil" })}
        style={SAFE_BOTTOM}
        className="fixed right-6 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-brand-button text-brand-button-foreground shadow-lg shadow-black/20 transition hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-button/60 focus-visible:ring-offset-2 lg:hidden"
      >
        <WhatsAppIcon className="h-6 w-6" />
      </a>
    </>
  );
}
